/* eslint react/prop-types: 0 */
import React from 'react'
import classnames from 'classnames'
import debounce from './debounce.js'
import initialState from './initial-state.js'
import {
  canGoNext,
  changeSlide,
  extractObject,
  getOnDemandLazySlides,
  getPostClones,
  getPreClones,
  getTrackCSS,
  getTrackLeft,
  initializedState,
  keyHandler,
  slideHandler,
  swipeEnd,
  swipeMove,
  swipeStart,
} from './utils.js'

import Track from './track.js'

class InnerSlider extends React.Component {
  constructor(props) {
    super(props)
    this.list = null
    this.track = null
    this.state = {
      ...initialState,
      currentSlide: this.props.initialSlide,
      slideCount: React.Children.count(this.props.children),
    }
    this.callbackTimers = []
    this.clickable = true
    this.debouncedResize = null
    const ssrState = this.ssrInit()
    this.state = { ...this.state, ...ssrState }
  }

  listRefHandler = (ref) => {
    this.list = ref
  }

  trackRefHandler = (ref) => {
    this.track = ref
  }

  componentDidMount = () => {
    const slidesToLoad = getOnDemandLazySlides({
      ...this.props,
      ...this.state,
    })
    if (slidesToLoad.length > 0) {
      this.setState((prevState) => ({
        lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad),
      }))
    }
    const spec = { listRef: this.list, trackRef: this.track, ...this.props }
    this.updateState(spec, true, () => {
      // eslint-disable-next-line no-unused-expressions
      this.props.autoplay && this.autoPlay('update')
    })
    this.ro = new ResizeObserver(() => {
      if (this.state.animating) {
        this.onWindowResized(false) // don't set trackStyle hence don't break animation
        this.callbackTimers.push(setTimeout(() => this.onWindowResized(), this.props.speed))
      } else {
        this.onWindowResized()
      }
    })
    this.ro.observe(this.list)
  }

  componentWillUnmount = () => {
    if (this.animationEndCallback) {
      clearTimeout(this.animationEndCallback)
    }
    if (this.lazyLoadTimer) {
      clearInterval(this.lazyLoadTimer)
    }
    if (this.callbackTimers.length) {
      this.callbackTimers.forEach((timer) => clearTimeout(timer))
      this.callbackTimers = []
    }
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
    }
    this.ro.disconnect()
  }

  // eslint-disable-next-line react/sort-comp
  didPropsChange(prevProps) {
    let setTrackStyle = false
    for (const key of Object.keys(this.props)) {
      // eslint-disable-next-line no-prototype-builtins
      if (!prevProps.hasOwnProperty(key)) {
        setTrackStyle = true
        break
      }
      if (typeof prevProps[key] === 'object' || typeof prevProps[key] === 'function') {
        // eslint-disable-next-line no-continue
        continue
      }
      if (prevProps[key] !== this.props[key]) {
        setTrackStyle = true
        break
      }
    }
    return (
      setTrackStyle ||
      React.Children.count(this.props.children) !== React.Children.count(prevProps.children)
    )
  }

  componentDidUpdate = (prevProps) => {
    this.checkImagesLoad()
    const slidesToLoad = getOnDemandLazySlides({
      ...this.props,
      ...this.state,
    })
    if (slidesToLoad.length > 0) {
      this.setState((prevState) => ({
        lazyLoadedList: prevState.lazyLoadedList.concat(slidesToLoad),
      }))
    }
    const spec = {
      listRef: this.list,
      trackRef: this.track,
      ...this.props,
      ...this.state,
    }
    const setTrackStyle = this.didPropsChange(prevProps)
    // eslint-disable-next-line no-unused-expressions
    setTrackStyle &&
      this.updateState(spec, setTrackStyle, () => {
        if (this.state.currentSlide >= React.Children.count(this.props.children)) {
          this.changeSlide({
            message: 'index',
            index: React.Children.count(this.props.children) - this.props.slidesToShow,
            currentSlide: this.state.currentSlide,
          })
        }
        if (this.props.autoplay) {
          this.autoPlay('update')
        } else {
          this.pause('paused')
        }
      })
  }

  onWindowResized = (setTrackStyle) => {
    if (this.debouncedResize) this.debouncedResize.cancel()
    this.debouncedResize = debounce(() => this.resizeWindow(setTrackStyle), 50)
    this.debouncedResize()
  }

  resizeWindow = (setTrackStyle = true) => {
    const isTrackMounted = Boolean(this.track && this.track.node)
    // prevent warning: setting state on unmounted component (server side rendering)
    if (!isTrackMounted) return
    const spec = {
      listRef: this.list,
      trackRef: this.track,
      ...this.props,
      ...this.state,
    }
    this.updateState(spec, setTrackStyle, () => {
      if (this.props.autoplay) this.autoPlay('update')
      else this.pause('paused')
    })
    // animating state should be cleared while resizing, otherwise autoplay stops working
    this.setState({
      animating: false,
    })
    clearTimeout(this.animationEndCallback)
    delete this.animationEndCallback
  }

  updateState = (spec, setTrackStyle, callback) => {
    const updatedState = initializedState(spec)
    // eslint-disable-next-line no-param-reassign
    spec = { ...spec, ...updatedState, slideIndex: updatedState.currentSlide }
    const targetLeft = getTrackLeft(spec)
    // eslint-disable-next-line no-param-reassign
    spec = { ...spec, left: targetLeft }
    const trackStyle = getTrackCSS(spec)
    if (
      setTrackStyle ||
      React.Children.count(this.props.children) !== React.Children.count(spec.children)
    ) {
      updatedState.trackStyle = trackStyle
    }
    this.setState(updatedState, callback)
  }

  ssrInit = () => {
    const childrenCount = React.Children.count(this.props.children)
    const spec = { ...this.props, ...this.state, slideCount: childrenCount }
    const slideCount = getPreClones(spec) + getPostClones(spec) + childrenCount
    const trackWidth = (100 / this.props.slidesToShow) * slideCount
    const slideWidth = 100 / slideCount
    const trackLeft =
      (-slideWidth * (getPreClones(spec) + this.state.currentSlide) * trackWidth) / 100
    const trackStyle = {
      width: `${trackWidth}%`,
      left: `${trackLeft}%`,
    }
    return {
      slideWidth: `${slideWidth}%`,
      trackStyle,
    }
  }

  checkImagesLoad = () => {
    const images =
      (this.list && this.list.querySelectorAll && this.list.querySelectorAll('.slick-slide img')) ||
      []
    Array.prototype.forEach.call(images, (image) => {
      if (!image.onclick) {
        // eslint-disable-next-line no-param-reassign
        image.onclick = () => image.parentNode.focus()
      } else {
        const prevClickHandler = image.onclick
        // eslint-disable-next-line no-param-reassign
        image.onclick = (e) => {
          prevClickHandler(e)
          image.parentNode.focus()
        }
      }
      if (!image.onload) {
        // eslint-disable-next-line no-param-reassign
        image.onload = () => {
          this.callbackTimers.push(setTimeout(this.onWindowResized, this.props.speed))
        }
      }
    })
  }

  slideHandler = (index) => {
    const { asNavFor, speed } = this.props
    const { state, nextState } = slideHandler({
      index,
      ...this.props,
      ...this.state,
      trackRef: this.track,
    })
    if (!state) return
    this.setState(state, () => {
      // asNavForIndex check is to avoid recursive calls of slideHandler in waitForAnimate=false mode
      if (asNavFor && this.asNavForIndex !== index) {
        this.asNavForIndex = index
        asNavFor.innerSlider.slideHandler(index)
      }
      if (!nextState) return
      this.animationEndCallback = setTimeout(() => {
        const { animating, ...firstBatch } = nextState
        this.setState(firstBatch, () => {
          this.callbackTimers.push(setTimeout(() => this.setState({ animating }), 10))
          delete this.animationEndCallback
        })
      }, speed)
    })
  }

  changeSlide = (options) => {
    const spec = { ...this.props, ...this.state }
    const targetSlide = changeSlide(spec, options)
    if (targetSlide !== 0 && !targetSlide) return
    this.slideHandler(targetSlide)
    // eslint-disable-next-line no-unused-expressions
    this.props.autoplay && this.autoPlay('update')
  }

  clickHandler = (e) => {
    if (this.clickable === false) {
      e.stopPropagation()
      e.preventDefault()
    }
    this.clickable = true
  }

  keyHandler = (e) => {
    const dir = keyHandler(e, this.props.accessibility, this.props.rtl)
    // eslint-disable-next-line no-unused-expressions
    dir !== '' && this.changeSlide({ message: dir })
  }

  selectHandler = (options) => {
    this.changeSlide(options)
  }

  // eslint-disable-next-line class-methods-use-this
  disableBodyScroll = () => {
    window.ontouchmove = (e) => {
      // eslint-disable-next-line no-param-reassign
      e = e || window.event
      if (e.preventDefault) e.preventDefault()
      e.returnValue = false
    }
  }

  // eslint-disable-next-line class-methods-use-this
  enableBodyScroll = () => {
    window.ontouchmove = null
  }

  swipeStart = (e) => {
    if (this.props.verticalSwiping) {
      this.disableBodyScroll()
    }
    const state = swipeStart(e, this.props.swipe)
    // eslint-disable-next-line no-unused-expressions
    state !== '' && this.setState(state)
  }

  swipeMove = (e) => {
    const state = swipeMove(e, {
      ...this.props,
      ...this.state,
      trackRef: this.track,
      listRef: this.list,
      slideIndex: this.state.currentSlide,
    })
    if (!state) return
    if (state.swiping) {
      this.clickable = false
    }
    this.setState(state)
  }

  swipeEnd = (e) => {
    const state = swipeEnd(e, {
      ...this.props,
      ...this.state,
      trackRef: this.track,
      listRef: this.list,
      slideIndex: this.state.currentSlide,
    })
    if (!state) return
    const { triggerSlideHandler } = state
    delete state.triggerSlideHandler
    this.setState(state)
    if (triggerSlideHandler === undefined) return
    this.slideHandler(triggerSlideHandler)
    if (this.props.verticalSwiping) {
      this.enableBodyScroll()
    }
  }

  touchEnd = (e) => {
    this.swipeEnd(e)
    this.clickable = true
  }

  slickPrev = () => {
    // this and fellow methods are wrapped in setTimeout
    // to make sure initialize setState has happened before
    // any of such methods are called
    this.callbackTimers.push(setTimeout(() => this.changeSlide({ message: 'previous' }), 0))
  }

  slickNext = () => {
    this.callbackTimers.push(setTimeout(() => this.changeSlide({ message: 'next' }), 0))
  }

  slickGoTo = (slide) => {
    // eslint-disable-next-line no-param-reassign
    slide = Number(slide)
    if (Number.isNaN(slide)) return
    this.callbackTimers.push(
      setTimeout(
        () =>
          this.changeSlide({
            message: 'index',
            index: slide,
            currentSlide: this.state.currentSlide,
          }),
        0,
      ),
    )
  }

  play = () => {
    let nextIndex
    if (this.props.rtl) {
      nextIndex = this.state.currentSlide - this.props.slidesToScroll
    } else if (canGoNext({ ...this.props, ...this.state })) {
      nextIndex = this.state.currentSlide + this.props.slidesToScroll
    } else {
      return
    }

    this.slideHandler(nextIndex)
  }

  autoPlay = (playType) => {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
    }
    const { autoplaying } = this.state
    if (playType === 'update') {
      if (autoplaying === 'hovered' || autoplaying === 'focused' || autoplaying === 'paused') {
        return
      }
    } else if (playType === 'leave') {
      if (autoplaying === 'paused' || autoplaying === 'focused') {
        return
      }
    } else if (playType === 'blur') {
      if (autoplaying === 'paused' || autoplaying === 'hovered') {
        return
      }
    }
    this.autoplayTimer = setInterval(this.play, this.props.autoplaySpeed + 50)
    this.setState({ autoplaying: 'playing' })
  }

  pause = (pauseType) => {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
    const { autoplaying } = this.state
    if (pauseType === 'paused') {
      this.setState({ autoplaying: 'paused' })
    } else if (pauseType === 'focused') {
      if (autoplaying === 'hovered' || autoplaying === 'playing') {
        this.setState({ autoplaying: 'focused' })
      }
    } else if (autoplaying === 'playing') {
      // pauseType  is 'hovered'
      this.setState({ autoplaying: 'hovered' })
    }
  }

  onTrackOver = () => this.props.autoplay && this.pause('hovered')

  onTrackLeave = () =>
    this.props.autoplay && this.state.autoplaying === 'hovered' && this.autoPlay('leave')

  onSlideFocus = () => this.props.autoplay && this.pause('focused')

  onSlideBlur = () =>
    this.props.autoplay && this.state.autoplaying === 'focused' && this.autoPlay('blur')

  render = () => {
    const className = classnames('slick-slider', this.props.className, {
      'slick-vertical': this.props.vertical,
      'slick-initialized': true,
    })
    const spec = { ...this.props, ...this.state }
    let trackProps = extractObject(spec, [
      'cssEase',
      'speed',
      'infinite',
      'currentSlide',
      'lazyLoadedList',
      'rtl',
      'slideWidth',
      'slideHeight',
      'listHeight',
      'vertical',
      'slidesToShow',
      'slidesToScroll',
      'slideCount',
      'trackStyle',
      'unslick',
      'targetSlide',
      'useCSS',
    ])
    trackProps = {
      ...trackProps,
      onMouseEnter: this.onTrackOver,
      onMouseLeave: this.onTrackLeave,
      onMouseOver: this.onTrackOver,
    }

    let verticalHeightStyle = null

    if (this.props.vertical) {
      verticalHeightStyle = {
        height: this.state.listHeight,
      }
    }

    const listStyle = { ...verticalHeightStyle }
    let listProps = {
      className: 'slick-list',
      style: listStyle,
      onClick: this.clickHandler,
      onMouseDown: this.swipeStart,
      onMouseMove: this.state.dragging ? this.swipeMove : null,
      onMouseUp: this.swipeEnd,
      onMouseLeave: this.state.dragging ? this.swipeEnd : null,
      onTouchStart: this.swipeStart,
      onTouchMove: this.state.dragging ? this.swipeMove : null,
      onTouchEnd: this.touchEnd,
      onTouchCancel: this.state.dragging ? this.swipeEnd : null,
      onKeyDown: this.props.accessibility ? this.keyHandler : null,
    }

    let innerSliderProps = {
      className,
      dir: this.props.rtl ? 'rtl' : 'ltr',
      style: this.props.style,
    }

    if (this.props.unslick) {
      listProps = { className: 'slick-list' }
      innerSliderProps = { className }
    }
    return (
      <div {...innerSliderProps}>
        <div ref={this.listRefHandler} {...listProps}>
          <Track ref={this.trackRefHandler} {...trackProps}>
            {this.props.children}
          </Track>
        </div>
      </div>
    )
  }
}

export default InnerSlider
